// src/api/client.js
import axios from "axios";

/**
 * Normalize a configured origin/base:
 * - null/undefined/empty => returns empty string (use same-origin)
 * - strips trailing slashes
 * - strips trailing '/api' to avoid double '/api' when callers use '/api/...' paths
 */
function normalizeBase(raw) {
  if (!raw) return "";
  let v = String(raw).trim();
  if (!v) return "";
  // remove trailing slash(es)
  v = v.replace(/\/+$/, "");
  // if it ends with '/api', remove that too
  v = v.replace(/\/api$/i, "");
  return v;
}

// Prefer REACT_APP_API_BASE, fall back to REACT_APP_API_URL for convenience.
// If neither is set, BASE will be empty string => use same origin.
const RAW_BASE = process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_URL || "";
const BASE = normalizeBase(RAW_BASE);

/**
 * axios baseURL:
 * - If BASE is empty string, we do not provide a remote origin so requests like
 *   client.get("/api/...") go to the same origin (recommended when nginx serves both).
 * - If BASE is "https://oldmachinemarket.in", axios will call "https://oldmachinemarket.in" + "/api/..."
 */
const client = axios.create({
  baseURL: BASE || '', // use same-origin if no BASE set — callers should include '/api/...' explicitly
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});


/* ---------------------- Request interceptor (token attach) ---------------------- */
client.interceptors.request.use(
  (config) => {
    try {
      const rawUrl = String(config.url || "");
      let path = rawUrl;

      // If absolute URL was given in code, extract its pathname to decide admin vs user
      if (rawUrl.startsWith("http://") || rawUrl.startsWith("https://")) {
        try {
          path = new URL(rawUrl).pathname;
        } catch (e) {
          path = rawUrl;
        }
      }

      const isAdminApi =
        path.startsWith("/api/admin") ||
        path.startsWith("/api/admin") ||
        path.startsWith("/api/admin-auth") ||
        path.startsWith("/api/admin-auth");

      const adminTokenRaw = (typeof window !== "undefined" && window.localStorage) ? localStorage.getItem("admin_token") : null;
      const userTokenRaw = (typeof window !== "undefined" && window.localStorage) ? localStorage.getItem("token") : null;

      const adminToken = adminTokenRaw ? String(adminTokenRaw).trim() : null;
      const userToken = userTokenRaw ? String(userTokenRaw).trim() : null;

      const tokenToUse = isAdminApi ? (adminToken || userToken) : (userToken || adminToken);

      if (tokenToUse && tokenToUse !== "null" && tokenToUse !== "undefined" && tokenToUse !== "") {
        config.headers = config.headers || {};
        if (!config.headers.Authorization && !config.headers.authorization) {
          config.headers.Authorization = `Bearer ${tokenToUse}`;
        }
      }
    } catch (e) {
      // don't break requests if interceptor fails
      // eslint-disable-next-line no-console
      console.warn("client interceptor error", e);
    }
    return config;
  },
  (err) => Promise.reject(err)
);

/* ---------------------- Response interceptor: auth handling ---------------------- */
client.interceptors.response.use(
  (res) => res,
  (err) => {
    try {
      const status = err?.response?.status;
      const url = err?.config?.url || "";
      const isAuthMe = url.includes("/api/auth/me") || url.includes("/auth/me");

      if (status === 401 && isAuthMe) {
        if (typeof window !== "undefined") {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
        }
        // eslint-disable-next-line no-console
        console.warn("Auth expired: cleared local token");
      } else if (status === 401) {
        // eslint-disable-next-line no-console
        console.warn("401 on", url);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("response interceptor err", e);
    }
    return Promise.reject(err);
  }
);

if (typeof window !== "undefined") window.__appClient = client;

export default client;